import { Checkbox, PrimaryButton } from '@fluentui/react';
import { FilterManager } from '@v2/components/controls/FilterManager/FilterManager';
import { FormControl } from '@v2/components/controls/FormControl/FormControl';
import HSObjectPicker from '@v2/components/controls/implementations/HSObjectPicker';
import { AdvancedSettings } from '@v2/components/flowsteps/editors/common/AdvancedSettings/AdvancedSettings';
import { FlowstepButtons } from '@v2/components/flowsteps/editors/common/FlowstepButtons/FlowstepButtons';
import { FlowstepContainer } from '@v2/components/flowsteps/editors/common/FlowstepContainer/FlowstepContainer';
import { PullOptions } from "@v2/components/flowsteps/editors/common/PullOptions/PullOptions";
import { getOperatorsForType } from './filters/hs-operators';
import { PropertyEdit } from './PropertyEdit/PropertyEdit';
import TitledPage from '@v2/components/layout/TitledPage';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { FilterCondition, PullMode } from 'shared/types/flowstep';
import GroupPane from 'src/components/base/GroupPane';
import { ModalPage } from 'src/components/base/Page';
import SearchableComboBox from 'src/components/base/SearchableComboBox';
import { NEW_WORKSHEET_ID } from 'src/v2/components/base/CellAddressPicker/hooks/useCellAddressValidation';
import { useStores } from 'src/v2/hooks/useStores';
import { FieldPicker } from "./FieldPicker/FieldPicker";
import { PullObjectsHSState } from './pull-objects-hs-state';
import { useStyles } from './PullObjectHS.styles';
import UnsavedChangesDialog from "@v2/hooks/UnsavedChangesDialog";
import { renderEditorForTypeAndOperator } from 'src/v2/components/controls/editors/hs-editors';
import { pullModes } from "@v2/components/flowsteps/editors/common/states/pull-settings-state";

interface PullObjectsHSProps {
  id?: string;
  createNew?: boolean;
}

const PullObjectsHS: React.FC<PullObjectsHSProps> = ({ id, createNew }) => {
  const styles = useStyles();
  const { officeStore, requestStore } = useStores();

  const [state] = useState(() => new PullObjectsHSState());

  const [isFilterManagerOpen, setFilterManagerOpen] = useState(false);
  const [optionsCollapsed, setOptionsCollapsed] = useState(true);
  const [advancedCollapsed, setAdvancedCollapsed] = useState(true);

  useEffect(() => {
    (async () => {
      await state.loadFlowstep(id, createNew);
    })();
  }, [id, createNew]);

  const handleFilterApply = (conditions: FilterCondition[]) => {
    state.pullSettings.conditions = conditions;
    setFilterManagerOpen(false);
  };

  const getOperators = (type: string) => {
    return getOperatorsForType(type);
  };

  let sectionNumber = 1;

  useEffect(() => {
    const error = state.pullSettings.objectLoadingError || state.pullSettings.objectFieldsLoadingError;
    if (error) {
      requestStore.raise(error, true);
    }
  }, [state.pullSettings.objectLoadingError, state.pullSettings.objectFieldsLoadingError])

  const title = `${state.isNewFlowstep ? 'New: ' : ''}From ${state.pullSettings.selectedObject?.label || 'Objects and Fields'}`;

  return (
    <>
      <UnsavedChangesDialog
        onSave={() => state.saveFlowstep()}
        isNotSaved={() => state.isNotSaved}
      />
      <TitledPage title={title}>
        <FlowstepContainer>
          <PropertyEdit
            label="Create new worksheet"
            tooltip={"We'll create a new worksheet for you and pull the data there"}
          >
            <Checkbox
              checked={state.pullSettings.createNewSheet}
              onChange={(_, checked) => state.pullSettings.createNewSheet = !!checked}
            />
          </PropertyEdit>

          <FormControl label={`${sectionNumber++}. Select the object to get data from`}>
            <HSObjectPicker
              data={state.pullSettings.objectLists.data}
              value={state.pullSettings.selectedObject}
              onChange={(_, newValue) => state.pullSettings.selectedObject = newValue}
              disabled={state.pullSettings.objectLists.isLoading}
              errorMessage={state.validationErrors.get('object-type-not-selected')}
            />
          </FormControl>

          <FormControl label={`${sectionNumber++}. Select fields to return`}>
            <FieldPicker
              items={state.pullSettings.objectFields.data}
              selectedFieldNames={state.pullSettings.selectedFieldNames}
              onChange={(value) => state.pullSettings.selectedFieldNames = value}
              disabled={!state.pullSettings.objectFields.isReady}
            />
          </FormControl>

          <FormControl label={`${sectionNumber++}. Setup filters (optional)`}>
            <PrimaryButton
              onClick={() => setFilterManagerOpen(true)}
              className={styles.filterManagerButton}
              disabled={!state.pullSettings.objectFields.data?.length}
            >
              Filter manager ({state.pullSettings.conditions.length})
            </PrimaryButton>
          </FormControl>

          <ModalPage
            isOpen={isFilterManagerOpen}
            title="Filter manager"
            onClose={() => setFilterManagerOpen(false)}
            helpLink={undefined}
            component={() => (
              <FilterManager
                objectFields={state.pullSettings.objectFields.data}
                conditions={state.pullSettings.conditions}
                getFieldOperators={getOperators}
                getFieldEditor={renderEditorForTypeAndOperator}
                onApply={handleFilterApply}
                allowEmptyValues={false}
              />
            )}
          />

          <FormControl label={`${sectionNumber++}. Select pull mode`}>
            <SearchableComboBox
              options={pullModes}
              selectedKey={state.pullSettings.pullMode}
              onChange={(_, selectedItem) => state.pullSettings.pullMode = selectedItem.key}
              placeholder="Pull mode option"
            />
            <div className={styles.fieldDescription}>
              {pullModes.find(mode => mode.key === state.pullSettings.pullMode)?.description}
            </div>
            {state.pullSettings.pullMode === PullMode.APPEND &&
              <PropertyEdit
                label={'Add Date/Time column'}
                tooltip={'Create a column with the date and time of execution for each run.'}
              >
                <Checkbox
                  checked={state.pullSettings.withDateTimeColumn}
                  onChange={(_, checked) => state.pullSettings.withDateTimeColumn = !!checked}
                  className={styles.pullModeCheckbox}
                />
              </PropertyEdit>
            }
          </FormControl>

          <GroupPane
            title="Options"
            collapsed={optionsCollapsed}
            onCollapsed={(collapsed: boolean) => setOptionsCollapsed(collapsed)}
            tooltip=""
            className={styles.groupPane}
          >
            <PullOptions state={state.pullOptions} />
            <PropertyEdit
              label="Force formula recalculation when done"
              tooltip={'Recalculates all workbook formulas and pivot tables after execution of this flow step'}
            >
              <Checkbox
                checked={state.recalculateFormulas}
                onChange={(_, checked) => state.recalculateFormulas = !!checked}
              />
            </PropertyEdit>
          </GroupPane>

          <GroupPane
            title="Advanced"
            collapsed={advancedCollapsed}
            onCollapsed={(collapsed: boolean) => setAdvancedCollapsed(collapsed)}
            tooltip=""
            className={styles.groupPane}
          >
            <AdvancedSettings
              baseEditorState={state}
              namePlaceholder={state.generatedFlowstepName}
              timestampCellProps={{
                allowNewSheet: true,
                getTimestampSheetId: () => state.getTimestampSheetId(),
              }}
            />
          </GroupPane>

          <FlowstepButtons
            editorState={state}
            disabled={state.isFlowstepButtonsDisabled}
          />
        </FlowstepContainer>
      </TitledPage>
    </>
  );
};

export default observer(PullObjectsHS);
